import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { joinURL } from 'ufo';
import useUserPlatform from '@/hooks/use-user-platform';
import {
  BASE_URL,
  BRAND_STORAGE_KEY,
  BUILD_EMBED_TYPE,
  EXTRAS_STORAGE_KEY,
  REDIRECT_TO_STORAGE_KEY,
} from '@/utils/constants';

export function usePullToRefresh(enable = true) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isIOS, mode, isEmbedded, isAndroid } = useUserPlatform();
  const location = useLocation();

  const shouldEnable = isIOS ? mode !== 'web' : isAndroid ? isEmbedded : false;

  return usePullToRefreshHook({
    onRefresh: () => {
      Promise.allSettled([
        queryClient.refetchQueries({ type: 'active' }),
        queryClient.resetQueries({ type: 'inactive' }),
      ]).catch(console.warn);

      const extras = sessionStorage.getItem(EXTRAS_STORAGE_KEY);
      const brand = localStorage.getItem(BRAND_STORAGE_KEY);
      if (isEmbedded && extras) {
        const { search, from } = JSON.parse(extras);
        const url = new URL(window.location.href);
        if (BUILD_EMBED_TYPE === 'embed-web') url.pathname = BASE_URL;
        url.searchParams.set('search', search);
        url.searchParams.set('from', from);
        url.searchParams.set('brand', brand || '');
        url.searchParams.set('buster', Date.now().toString());
        sessionStorage.setItem(
          REDIRECT_TO_STORAGE_KEY,
          joinURL('/', BASE_URL, location.pathname + location.search + location.hash),
        );
        window.location.replace(url);
      } else {
        navigate(0);
      }
    },
    startingThreshold: 80,
    refreshThreshold: 240,
    maximumPullLength: 320,
    isDisabled: !(shouldEnable && enable),
  });
}

//
// shamelessly taken from https://github.com/Senbonzakura1234/use-pull-to-refresh/blob/main/src/index.ts
//
export const DEFAULT_MAXIMUM_PULL_LENGTH = 240;
export const DEFAULT_REFRESH_THRESHOLD = 180;

export type UsePullToRefreshParams = {
  onRefresh: () => void | Promise<void>;
  // default value is 240
  maximumPullLength?: number;
  // default value is 180
  refreshThreshold?: number;
  // default value is 180;
  startingThreshold?: number;
  isDisabled?: boolean;
};
export type UsePullToRefreshReturn = {
  isRefreshing: boolean;
  pullPosition: number;
};
export type UsePullToRefresh = (params: UsePullToRefreshParams) => UsePullToRefreshReturn;

const isValid = (maximumPullLength: number, refreshThreshold: number) => maximumPullLength >= refreshThreshold;

export const usePullToRefreshHook: UsePullToRefresh = ({
  onRefresh,
  maximumPullLength = DEFAULT_MAXIMUM_PULL_LENGTH,
  refreshThreshold = DEFAULT_REFRESH_THRESHOLD,
  startingThreshold = DEFAULT_REFRESH_THRESHOLD,
  isDisabled = false,
}) => {
  const [pullStartPosition, setPullStartPosition] = useState(-1); // use -1 to indicate that the pull has not started
  const [pullPosition, setPullPosition] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onPullStart = useCallback(
    ({ targetTouches }: TouchEvent) => {
      if (isDisabled) return;
      const touch = targetTouches[0];

      if (touch && touch.screenY <= startingThreshold) setPullStartPosition(touch.screenY);
      else setPullStartPosition(-1);
    },
    [isDisabled, startingThreshold],
  );

  const onPulling = useCallback(
    ({ targetTouches }: TouchEvent) => {
      if (isDisabled) return;

      const touch = targetTouches[0];

      if (!touch || pullStartPosition < 0) return;
      const currentPullLength = pullStartPosition < touch.screenY ? Math.abs(touch.screenY - pullStartPosition) : 0;

      if (currentPullLength <= maximumPullLength && pullStartPosition < window.screen.height / 3)
        setPullPosition(() => currentPullLength);
    },
    [isDisabled, maximumPullLength, pullStartPosition],
  );

  const onEndPull = useCallback(() => {
    if (isDisabled) return;

    setPullStartPosition(-1);
    setPullPosition(0);

    if (pullPosition < refreshThreshold) return;

    setIsRefreshing(true);
    setTimeout(async () => {
      await onRefresh();
      setIsRefreshing(false);
    }, 500);
  }, [isDisabled, onRefresh, pullPosition, refreshThreshold]);

  useEffect(() => {
    if (isDisabled) return;

    window.addEventListener('touchstart', onPullStart, { passive: true });
    window.addEventListener('touchmove', onPulling, { passive: true });
    window.addEventListener('touchend', onEndPull, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onPullStart);
      window.removeEventListener('touchmove', onPulling);
      window.removeEventListener('touchend', onEndPull);
    };
  }, [isDisabled, onEndPull, onPullStart, onPulling]);

  useEffect(() => {
    if (isValid(maximumPullLength, refreshThreshold) || process.env.NODE_ENV === 'production' || isDisabled) return;
    console.warn(
      'usePullToRefresh',
      `'maximumPullLength' (currently ${maximumPullLength})  should be bigger or equal than 'refreshThreshold' (currently ${refreshThreshold})`,
    );
  }, [maximumPullLength, refreshThreshold, isDisabled]);

  return { isRefreshing, pullPosition };
};
